import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Plug, RefreshCw, Undo2 } from "lucide-react";
import { BILLING_LABEL, cancelBilling, errText, fetchBillingFirms, fetchInvoicingQueue, proformaUrl, retryBilling, ron } from "@/lib/bf";
import BFInvoicingFirms from "./BFInvoicingFirms";
import BFStockTotal, { DemoTag } from "./BFStockTotal";
import BFReception from "./BFReception";
import BFTransfers from "./BFTransfers";
import BFReceivables from "./BFReceivables";
import BFAgentMessages from "./BFAgentMessages";

const TABS = [["de_trimis", "De trimis"], ["trimis", "Trimise"], ["eroare", "Erori"]];
const VIEWS = [["coada", "Comenzi"], ["stoc", "Stoc"], ["receptie", "Recepție"], ["transferuri", "Transferuri"], ["incasari", "Încasări"], ["mesaje", "Mesaje agenți"], ["firme", "Firme"]];

const Queue = () => {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("trimis");
  const load = useCallback(() => fetchInvoicingQueue().then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const undo = (r) => cancelBilling(r.id).then(() => { toast.success(`${r.no} scoasă din coadă`); load(); }).catch((e) => toast.error(errText(e, "Nu am putut anula")));
  const retry = (r) => retryBilling(r.id).then((b) => { b.status === "trimis" ? toast.success(`${r.no} trimisă în NeoManager`) : toast.error(b.error); load(); }).catch((e) => toast.error(errText(e, "Nu am putut retrimite")));
  const list = (rows || []).filter((r) => r.billing.status === tab);
  return (
    <section className="invite-box" data-testid="bf-inv-queue">
      <div className="bf-inv-tabs">
        {TABS.map(([id, label]) => (
          <button key={id} type="button" className={tab === id ? "is-on" : ""} onClick={() => setTab(id)} data-testid={`bf-inv-tab-${id}`}>
            {label} <b>{(rows || []).filter((r) => r.billing.status === id).length}</b>
          </button>
        ))}
      </div>
      {!rows && <p className="st-hint">Se încarcă…</p>}
      {rows && !list.length && <p className="st-hint" data-testid="bf-inv-empty">Nicio comandă în „{TABS.find((t) => t[0] === tab)[1]}”.</p>}
      <ul className="bfa-leads">
        {list.map((r) => (
          <li key={r.id} data-testid={`bf-inv-row-${r.no}`}>
            <div>
              <b>{r.no} · {r.salon || "Salon neînregistrat"}</b>
              <span>{r.date}{r.cui ? ` · CUI ${r.cui}` : ""} · {r.lines} linii{r.lines > 40 ? ` (${Math.ceil(r.lines / 40)} documente)` : ""} · {r.billing.firm_name} · trimisă de {r.billing.by || "-"}</span>
              {r.billing.neo_no && <span>NeoManager {r.billing.neo_no}{r.billing.transfers ? ` · ${r.billing.transfers} transfer(uri) între firme` : ""}</span>}
              {r.billing.error && <span className="bf-inv-err">{r.billing.error}</span>}
              {r.billing.warning && <span className="bf-inv-warn" data-testid={`bf-inv-warn-${r.no}`}>{r.billing.warning}</span>}
            </div>
            <div className="interested-actions">
              <span className={`bf-bill-chip is-${r.billing.status}`}>{BILLING_LABEL[r.billing.status]}</span>
              <span className="bfa-pf-total">{ron(r.total)}</span>
              <a className="st-btn st-btn--ghost" href={proformaUrl(r.id)} target="_blank" rel="noopener noreferrer" data-testid={`bf-inv-pdf-${r.no}`}><FileText size={14} /> PDF</a>
              {r.billing.status === "eroare" && <button type="button" className="st-btn st-btn--ghost" onClick={() => retry(r)} data-testid={`bf-inv-retry-${r.no}`}><RefreshCw size={14} /> Reîncearcă</button>}
              {r.billing.status !== "trimis" && <button type="button" className="st-btn st-btn--ghost" onClick={() => undo(r)} data-testid={`bf-inv-cancel-${r.no}`}><Undo2 size={14} /> Scoate din coadă</button>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default function BFInvoicing() {
  const [view, setView] = useState("coada");
  const [firms, setFirms] = useState([]);
  useEffect(() => { fetchBillingFirms().then((d) => setFirms(d.firms)).catch(() => {}); }, [view]);
  return (
    <>
      <section className="invite-box bf-inv-info" data-testid="bf-inv-info">
        <h3><Plug size={18} /> NeoManager · mediu de probă <DemoTag /></h3>
        <p className="st-hint">Totul funcționează cap-coadă pe date de probă: comanda trimisă la facturare scade stocul din cele mai vechi loturi (din una sau mai multe firme, cât e nevoie din fiecare), face transferurile între firme și creează proforma și factura „în NeoManager”. Chitanțele agenților scad soldul clientului pe loc. Când primim accesul real, schimbăm doar conectorul.</p>
      </section>
      <nav className="bf-inv-tabs bf-inv-views" data-testid="bf-inv-views">
        {VIEWS.map(([id, label]) => <button key={id} type="button" className={view === id ? "is-on" : ""} onClick={() => setView(id)} data-testid={`bf-inv-view-${id}`}>{label}</button>)}
      </nav>
      {view === "coada" && <Queue />}
      {view === "stoc" && <BFStockTotal admin firms={firms} />}
      {view === "receptie" && firms.length > 0 && <BFReception firms={firms} />}
      {view === "transferuri" && <BFTransfers />}
      {view === "incasari" && <BFReceivables admin />}
      {view === "mesaje" && <BFAgentMessages />}
      {view === "firme" && <BFInvoicingFirms />}
    </>
  );
}
